/** One provider setting on the model form, rendered by the field type the hosting declares. */

import type { ReactElement } from 'react';

import type { ProviderField } from '@/api/types';
import { FieldLabel } from '@/ui/FieldLabel';

interface InputProps {
  field: ProviderField;
  value: string | undefined;
  stored: boolean;
  /** Id of the hint's tooltip, when the field has one. */
  describedBy?: string;
  onChange: (value: string) => void;
}

function SelectInput({ field: f, value, describedBy, onChange }: InputProps) {
  return (
    <select id={`m-cfg-${f.key}`} aria-describedby={describedBy} value={value ?? f.default ?? ''} onChange={(e) => onChange(e.target.value)}>
      {f.options.map((o) => (
        <option key={o} value={o}>{o || 'Provider default'}</option>
      ))}
    </select>
  );
}

function SecretInput({ field: f, value, stored, describedBy, onChange }: InputProps) {
  return (
    <input
      id={`m-cfg-${f.key}`}
      aria-describedby={describedBy}
      type="password"
      autoComplete="off"
      required={f.required && !stored}
      value={value ?? ''}
      placeholder={stored ? 'Stored. Leave blank to keep it.' : f.placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function TextInput({ field: f, value, describedBy, onChange }: InputProps) {
  return (
    <input
      id={`m-cfg-${f.key}`}
      aria-describedby={describedBy}
      type="text"
      required={f.required}
      value={value ?? ''}
      placeholder={f.placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

const INPUTS: Record<ProviderField['type'], (props: InputProps) => ReactElement> = {
  select: SelectInput,
  secret: SecretInput,
  text: TextInput,
};

export function ConfigField(props: Omit<InputProps, 'describedBy'>) {
  const { field: f } = props;
  // An unknown field type renders as text, as the server may declare new ones first.
  const Input = INPUTS[f.type] ?? TextInput;
  const hintId = `m-cfg-${f.key}-hint`;
  return (
    <div className="field">
      <FieldLabel htmlFor={`m-cfg-${f.key}`} id={hintId} label={f.label} hint={f.hint || undefined} />
      <Input {...props} describedBy={f.hint ? hintId : undefined} />
    </div>
  );
}
